import { STATUS_ICON, STATUS_TEXT_COLOR, STATUS_BADGE_BG } from "../api/constants/status";
import { formatDuration, timeAgo } from "../api/utils/format";
import { useTaskNodes, useUnblockedTaskIds, useExecByTask } from "../api/context/ProjectDashboardContext";

// Aliases matching the original local names used in JSX below
const statusIcon = STATUS_ICON;
const statusColor = STATUS_TEXT_COLOR;
const statusBg = STATUS_BADGE_BG;

export function ListView({ hideComplete }: { hideComplete: boolean }) {
  const tasksState = useTaskNodes();
  const unblockedState = useUnblockedTaskIds();
  const execState = useExecByTask();

  // While any selector is loading, show skeleton
  if (tasksState.status === "loading" || unblockedState.status === "loading" || execState.status === "loading") {
    return <div className="animate-pulse h-64 bg-zinc-900 rounded-lg" />;
  }

  // On error, show first error encountered
  if (tasksState.status === "error") return <div className="text-red-400 p-4">{tasksState.error}</div>;
  if (unblockedState.status === "error") return <div className="text-red-400 p-4">{unblockedState.error}</div>;
  if (execState.status === "error") return <div className="text-red-400 p-4">{execState.error}</div>;

  const tasks = tasksState.data;
  const readyIds = unblockedState.data; // Already a Set<string> from the selector
  const execByTask = execState.data;    // Record<string, ExecutionEntryVM> — no client-side Map needed

  const filtered = hideComplete
    ? tasks.filter((t) => t.status !== "complete" && t.status !== "deferred")
    : tasks;

  return (
    <div className="space-y-3">


      {filtered.length === 0 ? (
        <div className="text-zinc-600 text-center py-12">No tasks</div>
      ) : (
        <>
        {/* Mobile card layout */}
        <div className="sm:hidden space-y-2">
          {filtered.map((task) => {
            const exec = execByTask[task.taskId];
            const isReady = readyIds.has(task.taskId);
            const isRunning = exec?.status === "running";

            return (
              <div
                key={task.taskId}
                className={`border border-zinc-800 rounded-lg p-3 space-y-1.5 ${isReady ? "bg-indigo-500/5" : ""}`}
              >
                <div className="flex items-center gap-2">
                  <span className={`${statusColor[task.status] ?? "text-zinc-500"} ${task.status === "in_progress" ? "status-pulse" : ""}`}>
                    {statusIcon[task.status] ?? "?"}
                  </span>
                  <span className="font-mono text-sm text-zinc-300 font-semibold">{task.taskId}</span>
                  {task.humanGate && <span className="text-amber-500 text-xs">HG</span>}
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ml-auto ${statusBg[task.status] ?? ""}`}>
                    {task.status.replace("_", " ")}
                  </span>
                  {isReady && <span className="text-indigo-400 text-[10px] font-medium">READY</span>}
                </div>
                <p className="text-sm text-zinc-400 leading-snug">{task.subject}</p>
                <div className="flex items-center gap-3 text-xs text-zinc-500">
                  {exec ? (
                    <>
                      <span className="flex items-center gap-1">
                        {isRunning && <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />}
                        {timeAgo(exec.startedAt)}
                      </span>
                      {exec.durationMs != null && <span className="tabular-nums">{formatDuration(exec.durationMs)}</span>}
                    </>
                  ) : (
                    <span>{"\u2014"}</span>
                  )}
                </div>
                {exec?.error && (
                  <p className="text-xs text-red-400 truncate">{exec.error}</p>
                )}
              </div>
            );
          })}
        </div>

        {/* Desktop table layout */}
        <div className="hidden sm:block border border-zinc-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-900/50 text-zinc-500 text-xs uppercase tracking-wider">
                <th className="px-4 py-2.5 text-left w-5"></th>
                <th className="px-4 py-2.5 text-left">Task</th>
                <th className="px-4 py-2.5 text-left">Subject</th>
                <th className="px-4 py-2.5 text-left">Status</th>
                <th className="px-4 py-2.5 text-left">Last Run</th>
                <th className="px-4 py-2.5 text-right">Duration</th>
                <th className="px-4 py-2.5 text-left">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/50">
              {filtered.map((task) => {
                const exec = execByTask[task.taskId];
                const isReady = readyIds.has(task.taskId);
                const isRunning = exec?.status === "running";

                return (
                  <tr
                    key={task.taskId}
                    className={`hover:bg-zinc-900/30 ${isReady ? "bg-indigo-500/5" : ""}`}
                  >
                    <td className={`px-4 py-2.5 text-center ${statusColor[task.status] ?? "text-zinc-500"} ${task.status === "in_progress" ? "status-pulse" : ""}`}>
                      {statusIcon[task.status] ?? "?"}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-zinc-400 whitespace-nowrap">
                      {task.taskId}
                      {task.humanGate && (
                        <span className="text-amber-500 text-xs ml-1" title="Human gate">HG</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 truncate max-w-[300px]">{task.subject}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${statusBg[task.status] ?? ""}`}>
                        {task.status.replace("_", " ")}
                      </span>
                      {isReady && (
                        <span className="text-indigo-400 text-[10px] font-medium ml-1.5">READY</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-zinc-500 whitespace-nowrap">
                      {exec ? (
                        <span className="flex items-center gap-1.5">
                          {isRunning && (
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                          )}
                          {timeAgo(exec.startedAt)}
                        </span>
                      ) : (
                        "\u2014"
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-zinc-400 whitespace-nowrap">
                      {exec?.durationMs != null
                        ? formatDuration(exec.durationMs)
                        : isRunning
                          ? formatDuration(Date.now() - exec.startedAt)
                          : "\u2014"}
                    </td>
                    <td className="px-4 py-2.5 text-red-400 truncate max-w-[200px]">
                      {exec?.error ?? ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  );
}
